// ====================================================
// src/components/block/liveStreamManagement/audioEffects/AudioEffect.ts
//
// Base commune à tous les effets audio du panneau "Effets audio" de SourceConfigModal
// (low-cut, reverb, compresseur, noise gate...) — même principe que VideoEffect côté
// vidéo. Un nouvel effet s'ajoute en créant une classe qui étend AudioEffect et en
// l'inscrivant dans registry.ts ; la modale ne boucle que sur ce registre, elle ne
// référence jamais un effet par son nom.
//
// Différence avec VideoEffect : pas de rendu par frame (composite()) — un effet audio
// s'applique une fois, au vrai graphe Web Audio (AudioSourceRegistry), via apply().
// Les effets audio ne sont PAS mutuellement exclusifs (contrairement aux effets vidéo,
// où un seul peut être actif) : plusieurs peuvent tourner en même temps sur une même
// source, chacun gardant son propre booléen "actif" (porté par SourceAudioEffects,
// hors de ce module) et sa propre config (SourceAudioConfig.audioEffectConfigs).
// ====================================================

import React from 'react';
import { AudioSourceRegistry } from '../../../../dataTypes/liveStreamManagment/audioPipeline/AudioSourceRegistry';

export interface AudioEffectControlProps<TConfig> {
  config: TConfig;
  onChange: (config: TConfig) => void;
}

export abstract class AudioEffect<TConfig = unknown> {
  abstract readonly id: string;
  abstract readonly label: string;
  abstract readonly icon: React.FC<{ style?: React.CSSProperties }>;
  abstract readonly defaultConfig: TConfig;
  /** Repris dans l'info-bulle du bouton — description courte d'une ligne. */
  abstract readonly description: string;

  // Effet pas encore implémenté côté pipeline (compresseur, noise gate) : ses contrôles
  // restent visibles mais grisés, cf. disabledReason affiché à côté du libellé — même
  // convention que VideoEffect (Makeup/Avatar avant leur implémentation).
  readonly disabled: boolean = false;
  readonly disabledReason?: string;

  /**
   * Applique ce réglage au VRAI graphe audio de la source (pas une préview locale — il
   * n'y en a pas pour l'audio, contrairement à la vidéo : le retour son de la régie
   * suffit à s'entendre). Appelé à chaque (re)connexion et à chaque changement de
   * réglage — cf. useAudioMetering.applyStoredLevels, qui boucle sur AUDIO_EFFECTS.
   */
  apply(_registry: AudioSourceRegistry, _regId: string, _enabled: boolean, _config: TConfig): void {}

  abstract renderControls(props: AudioEffectControlProps<TConfig>): React.ReactNode;
}
